/**
 * The abstraction gate.
 *
 * Generalize on the way in, specialize on the way out. This is the write path
 * into the shared pool, and it is a HARD GATE, not a best effort: a spec that
 * fails abstraction does not enter the pool.
 *
 * The invariant it enforces, which is the one that keeps the whole pool safe:
 *
 *   A pool genome contains SLOT ROLES ONLY. The slot -> property-key map is
 *   private, per-tenant, and never pooled.
 *
 * Roles are already what a spec binds to, so most of the safety is structural
 * rather than earned here. What this gate removes is everything that leaked in
 * around the edges: the manifest reference, module config outside each
 * capability's sanitization allowlist, and any free text at all.
 *
 * Two of those edges are closed by CONSTRUCTION rather than by filtering, since
 * a filter only catches what someone thought to look for:
 *
 *   - every scalar on a genome is NAMED from a closed set (`fromClosedSet`),
 *     never copied out of the spec;
 *   - every list whose order means nothing is emitted in a canonical order
 *     (`canonicalOrder`), because `log2(16!)` bits of permutation is a payload
 *     even when every element passes its own check.
 */

import type { TLayoutSpec, TNode, TQuery } from './schema.js';
import * as V from './vocabulary.js';
import { countValues, MAX_VALUES } from './grammar.js';
import { validateSpec, type Problem } from './validate.js';
import {
  lookupModule, registeredModuleIds, type ModuleDescriptor,
} from './module-registry.js';

export interface PoolGenome {
  vocabularyVersion: string;
  target: TLayoutSpec['target'];
  density: V.Density;
  typography: V.TypeSystem;
  palette: V.Palette;
  structure: unknown;
  /** The declared partition, with its branch ids renumbered like the tree they
   *  name. Structural vocabulary — an index and canonical ids, never a value.
   *  Absent when the spec declares none, which is every spec before 0.15.0. */
  partition?: {
    readonly valueFrom: number;
    readonly branches: readonly string[];
    readonly unset: { readonly show: string };
  };
  /** The named app look the spec was generated under (decision 0055) — a
   *  closed word, so the pool can learn that people keep this shape AS
   *  Obsidian without the genome carrying anything of theirs. Absent when the
   *  spec names none. The template's version is provenance for reopening and
   *  not a taste fact, so it stays out. */
  template?: V.AppTemplate;
  /** Shape metadata retrieval matches on. Bucketed, so the pool supplies priors
   *  to the design compiler rather than finished layouts — otherwise every user
   *  converges on the same popular handful and the taste model stops mattering.
   *
   *  These buckets are an INDEXING convenience, not a privacy boundary:
   *  `structure` below carries the whole tree, so the exact count and depth are
   *  derivable from the genome regardless of what this says. */
  shape: {
    depth: number;
    nodeCount: number;
    capabilities: string[];
    roleKinds: V.RoleKind[];
    writable: boolean;
  };
}

/**
 * Retrieval quantization. **Not a privacy control** — read the second paragraph
 * before relying on it.
 *
 * `PoolGenome.shape` described itself as "deliberately coarse" while both
 * numbers were exact counters, so bucketing them is still right: a retriever
 * wants a size class, not a census, and buckets are reported as their **lower
 * bound** so the value stays a number, stays monotone, and can still be ordered.
 *
 * But it does not hide anything. @Codex's review of PR #27: the genome carries
 * the complete projected `structure` immediately beside this metadata, and a
 * recipient can walk it and count. The exact node count and maximum depth are
 * recoverable in a few lines, so describing these buckets as fingerprint
 * prevention would repeat the exact error they were introduced to fix — a
 * comment claiming a property the code does not have.
 *
 * Genuine protection has to apply to the WHOLE genome: the aggregation
 * threshold that withholds a rare genome until enough similar ones exist, or
 * generalization of `structure` itself. Neither exists. Until one does, nothing
 * should be pooled for real — see `Agents/Tasks/0001-layout-spec-v0.md`.
 */
const NODE_BUCKETS = [1, 4, 8, 16, 32, 64, 128, 256, 512, 1024, 2048] as const;
const DEPTH_BUCKETS = [1, 3, 5, 9] as const;

const bucket = (n: number, bounds: readonly number[]): number => {
  let out = bounds[0]!;
  for (const b of bounds) if (n >= b) out = b;
  return out;
};

/**
 * How many refusal reasons the error carries.
 *
 * Decision `0044` requires each validation problem's code and path to survive
 * into the refusal, and `validateSpec()` already caps itself at 200 — but a
 * seven-branch union multiplies errors at every bad node, so a single
 * misspelled `via` can arrive as a hundred and ninety problems about the
 * branches it did not match. All of them name the same field.
 *
 * Sixteen is enough to see a real cluster and short enough that the message is
 * readable. `totalProblems` keeps the honest count, so nothing is silently
 * rounded down to what fits.
 */
const MAX_REPORTED = 16;

/**
 * The cause first, and the union's noise after it.
 *
 * Ajv reports every branch of a `oneOf` it tried, so ONE misspelled operator
 * inside a collection query arrives as forty-six problems — of which
 * forty-five are `shape @/root`, the root failing to match any branch, and
 * exactly one is `shape @/root/query/filter/where/op`, the field that is
 * actually wrong. Measured on `collectionMediaFirst`: 46 problems, 4 distinct.
 *
 * A cap of sixteen applied to that list keeps sixteen copies of the noise and
 * discards the cause — a refusal that is correct and useless, and one decision
 * `0044` would not accept: it requires the error to *"identify the rejected
 * field"*. So the list is deduplicated and ordered deepest path first, which
 * is the one heuristic that survives a union cascade: the branch failures name
 * the container, the real problem names the leaf.
 *
 * Not a sort by severity, which would be a second opinion about what the
 * validator meant. Depth is a fact about the path it already reported.
 */
/** A refusal may not echo the value it refused — see the boundary in `project`. */
const WITHHELD = '(withheld: a refusal must not repeat the value it refused)';

const rankProblems = (problems: readonly Problem[]): Problem[] => {
  const seen = new Set<string>();
  const distinct = problems.filter((p) => {
    const key = `${p.code}\u0000${p.path}\u0000${p.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  // Stable, so equal-depth problems keep the order the validator found them in.
  return distinct.sort((a, b) => b.path.split('/').length - a.path.split('/').length);
};

export class AbstractionError extends Error {
  /**
   * The validator's own problems, structurally — `0044`: *"must preserve
   * enough of each validation problem—at least its code and path—to identify
   * the rejected field."* A joined string cannot be read back by a caller
   * deciding which field to point at, so the strings in `leaks` are for a
   * person and this is for the program. Empty for a leak refusal, which is
   * the projector's own finding rather than the validator's.
   */
  readonly problems: readonly Problem[];
  /** Before the cap. Equal to `problems.length` unless the cap bit. */
  readonly totalProblems: number;

  constructor(readonly leaks: string[], problems: readonly Problem[] = []) {
    const total = problems.length;
    const kept = problems.slice(0, MAX_REPORTED);
    const shown = total > MAX_REPORTED
      ? [...leaks.slice(0, MAX_REPORTED), `… and ${total - MAX_REPORTED} more`]
      : leaks;
    super(total > 0
      ? `spec cannot enter the pool: ${total} validation problem(s)\n  - ${shown.join('\n  - ')}`
      : `spec cannot enter the pool: ${leaks.length} leak(s)\n  - ${leaks.join('\n  - ')}`);
    this.name = 'AbstractionError';
    this.leaks = shown;
    this.problems = kept;
    this.totalProblems = total;
  }
}

/**
 * Defence in depth only — never the last line.
 *
 * The previous version (64 chars of `[a-z0-9._-]`) was a *prose detector* being
 * used as a *privacy filter*. It caught `"Jane Doe's diagnosis"` and passed
 * `jane.doe-acme.com_salary-band-7_dx-stage4`, because dots, dashes and
 * underscores are exactly the separators real identifiers use.
 *
 * It is now tight enough to be worth having — 32 chars, at most one separator —
 * but every field that reaches a genome is constrained by a closed vocabulary
 * or a positional index instead of by this.
 */
const TOKENISH = /^[a-z0-9]+[._-]?[a-z0-9]*$/i;

function isFreeText(value: string): boolean {
  return value.length > 32 || !TOKENISH.test(value);
}

/**
 * Name a value from a closed set, or record a leak.
 *
 * The gate is documented as callable WITHOUT validation, so it cannot lean on
 * the schema's enums having run: `enumOf(V.DENSITIES)` constrains a *validated*
 * spec and says nothing about the one in front of us. Everything scalar on a
 * genome goes through here, so a value nobody checked cannot ride in beside a
 * key everybody did.
 *
 * The placeholder return only ever reaches a genome that is thrown away — a
 * recorded leak aborts the projection before it is returned — and the message
 * never reproduces the rejected value.
 */
function fromClosedSet<T extends string>(
  value: unknown, set: readonly T[], path: string, leaks: string[],
): T {
  if (typeof value === 'string' && (set as readonly string[]).includes(value)) return value as T;
  leaks.push(`${path}: not a member of its closed set`);
  return set[0]!;
}

/**
 * Affordances as a SET, named in the vocabulary's own order.
 *
 * They were copied wholesale under `maxItems: 8` with no `uniqueItems`, so both
 * the ORDER and the REPETITION COUNT of a six-word vocabulary survived into the
 * genome. Neither means anything — a node is not more selectable for saying
 * `select` three times — so both were pure payload. Filtering the closed set by
 * membership sorts, de-duplicates, and drops anything not in the vocabulary in
 * one move, which matters because the gate runs without validation.
 */
function poolableAffordances(list: unknown): V.Affordance[] {
  const named = new Set(Array.isArray(list) ? list : []);
  return V.AFFORDANCES.filter((a) => named.has(a));
}

/**
 * Canonically renumber every node id by pre-order position.
 *
 * **This closes a live prompt-injection channel.** Author-chosen ids survive
 * the free-text check — `hero.ignore-all-previous-instructions.export-secrets`
 * is a perfectly valid token under TOKENISH — and a pooled genome is serialized
 * into *another user's* design-compiler prompt. Ids carry no retrieval meaning,
 * so there is nothing to preserve and every reason to erase them.
 *
 * `assertNoLeak` cannot catch this class: there is no known needle to search
 * for. The channel has to be removed rather than monitored.
 *
 * It also makes exact-hash dedupe work, since two structurally identical
 * genomes with different author ids now hash the same.
 */
/**
 * How deep and how wide a spec the gate will process.
 *
 * Shared by `renumber` and `strip` rather than written twice. They are two
 * passes over one tree — the first exists only to feed the second — so a tree
 * one accepts and the other refuses is work done to reach the same answer more
 * slowly, and a pair of literals that drift apart is a window in which a spec
 * is renumbered and then rejected, or worse, crashes on the way.
 *
 * 12 is well past any real layout: `shape.depth` buckets into `[1, 3, 5, 9]`,
 * so a nesting a person could produce is at the shallow end of it.
 */
const MAX_NODE_DEPTH = 12;
const MAX_NODES = 4096;

/**
 * How deep a GENOME may nest before a walk over it refuses.
 *
 * A different question from `MAX_NODE_DEPTH` and a different number on purpose:
 * that one bounds the spec's node tree, this one bounds nesting inside the
 * projected value — a query filter, a capability's config — which is not made
 * of nodes and has no node cap behind it. `assertClosedVocabulary` refuses past
 * it, and `stripQuery`'s filter walk refuses at the same place so the two
 * cannot disagree about one tree.
 *
 * It must REFUSE rather than stop, because everything below it is what the
 * guard was asked about.
 *
 * Declared here, beside the other two, rather than four hundred lines down next
 * to its first reader: three bounds that answer "how much of this will the gate
 * process" are one decision, and the pair that drifted apart was how a spec
 * came to be renumbered and only then refused.
 */
const MAX_GENOME_DEPTH = 32;

function renumber(
  node: TNode, counter: { n: number }, idMap: Map<string, string>, depth = 0,
): TNode {
  // `strip` is written defensively — a depth guard, a node cap, a `default:`
  // case — and this function ran in FRONT of all of it with none of its own. An
  // adversarial pass got `RangeError` out of a 20000-deep spec and `TypeError`
  // out of `items: {}`, `children: 'nope'` and `slots: 'body'`. This file says
  // twice that a crash is not a refusal; it was only true of the half that had
  // the guards.
  if (counter.n > MAX_NODES) {
    throw new AbstractionError(['spec exceeds the size the gate will process']);
  }
  /**
   * The DEPTH guard, which the size guard above is not.
   *
   * @Codex, P2 on #184: *"A roughly 2,500-level region tree is still below the
   * 20,000-value gate, but this recursive renumbering exhausts the JavaScript
   * stack before either the 4,096-node check or `strip()`'s depth refusal runs.
   * Because `project()` explicitly accepts inputs that have not passed schema
   * validation, such a tree produces a raw `RangeError` rather than the
   * promised abstraction refusal."*
   *
   * **The premise in that quote is no longer true, and the guard still is.**
   * Decision `0044` made `project()` validate first, so `max-depth` refuses a
   * 2,500-level tree before this runs. This stays under `0044` §3: it is a
   * bound on the projector's own recursion, it refuses with `AbstractionError`,
   * and it names an invariant validation does not own — a spec that is legal at
   * every level can still be a tree this function cannot walk, because the
   * schema's depth limit and this walk's stack are different quantities.
   *
   * Measured before it was fixed, and the window is narrower than "deep":
   *
   * ```
   * 2,500 nested regions   RangeError: Maximum call stack size exceeded
   * 5,000 nested regions   AbstractionError — countValues passes 20,000 first
   * ```
   *
   * So a tree has to be deep enough to exhaust the stack and shallow enough to
   * stay under `MAX_VALUES` and under 4,096 ids. Both existing guards look past
   * it: one counts values, the other counts ids, and neither counts nesting.
   *
   * The bound is `strip`'s own, shared as a constant rather than repeated —
   * this walk exists only to feed `strip`, so a tree it accepts and `strip`
   * then refuses is work done to reach the same answer more slowly. And the
   * refusal is worded identically, because from outside they are one gate.
   *
   * This is the THIRD time in this file. The external-reference scan above
   * already carries the note — *"the recursive version made a liar of it: a
   * two-node cyclic spec threw RangeError instead of AbstractionError"* — and
   * `assertClosedVocabulary` carries it for its own walk. A crash is not a
   * refusal, and every recursive descent over unvalidated input in this file
   * has had to learn it separately.
   */
  if (depth > MAX_NODE_DEPTH) {
    throw new AbstractionError(['spec exceeds the depth or size the gate will process']);
  }
  if (!node || typeof node !== 'object') {
    throw new AbstractionError(['spec contains a node that is not an object']);
  }
  const next = { ...node } as TNode & { id?: string };
  if ('id' in next && next.id !== undefined) {
    const canonical = `n${counter.n++}`;
    // An anchored placement REFERENCES another node by id, so erasing ids
    // without recording where they went would leave the anchor pointing at a
    // name that no longer exists anywhere in the genome. Pre-order means a
    // forward reference is not yet renumbered when it is read, so the map is
    // completed here and applied in `strip`, which runs afterwards.
    /**
     * The duplicate-authored-id refusal that stood here is GONE — `0044` §1.
     *
     * It was right, and the reason it was right is worth keeping: renumbering
     * ERASES the evidence, so two nodes sharing an authored id become `n3` and
     * `n7`, an anchor points at whichever was visited last, and nothing
     * downstream can tell the spec was ambiguous. A malformed input becomes a
     * well-formed genome, which is the one outcome this gate exists to prevent.
     *
     * But `0044` §1 removes a check *"whose only claim is to reject exactly
     * what `validateSpec()` rejects"*, once a control proves the validator
     * covers that input — and it names duplicate authored node ids explicitly.
     * The control is `pool.test.ts`'s *"refuses two nodes sharing an authored
     * id, as the validator already does"*, which asserts `duplicate-id`
     * arriving through the boundary with its path intact.
     *
     * §3 would have let it stay as a defensive check, and it does not qualify:
     * the ambiguity it describes can only arise from an input the validator
     * already refuses, so there is no control for it independent of validation
     * — and *"in case validation misses it"* is exactly what §3 rules out.
     */
    idMap.set(next.id, canonical);
    next.id = canonical;
  }

  switch (next.kind) {
    case 'region':
      if (!Array.isArray(next.children)) {
        throw new AbstractionError(['a region\'s children must be an array']);
      }
      next.children = next.children.map((c) => renumber(c, counter, idMap, depth + 1));
      break;
    case 'collection':
      if (!Array.isArray(next.items)) {
        throw new AbstractionError(['a collection\'s items must be an array']);
      }
      next.items = next.items.map((it) => ({
        ...it, spec: renumber((it ?? {}).spec as TNode, counter, idMap, depth + 1),
      }));
      break;
    case 'card':
      if (next.children) {
        if (!Array.isArray(next.children)) {
          throw new AbstractionError(['a card\'s children must be an array']);
        }
        next.children = next.children.map((c) => renumber(c, counter, idMap, depth + 1));
      }
      break;
    case 'capability':
      if (next.slots) {
        next.slots = Object.fromEntries(
          Object.entries(next.slots).map(([k, kids]) => {
            if (!Array.isArray(kids)) {
              throw new AbstractionError([`slot "${k}" must hold an array`]);
            }
            return [k, kids.map((c) => renumber(c, counter, idMap, depth + 1))];
          }),
        );
      }
      break;
    default:
      break;
  }
  return next;
}

/**
 * Canonical order for a list whose order carries nothing, plus exact-duplicate
 * removal.
 *
 * ORDER IS A CHANNEL. `capabilities` and `roleKinds` were already sorted; the
 * lists reached by `strip` were not, so a spec could say what it liked in the
 * permutation of 16 bindings (~44 bits) while every individual field passed its
 * closed-set check. Sorting the GENOME's copy costs nothing, because the genome
 * is a retrieval prior and is never rendered — the spec itself is untouched.
 *
 * Applied only where reordering provably changes no meaning: card bindings, the
 * operands of `all`/`any` (boolean AND and OR commute), and `aggregate` (a bag
 * of independent folds).
 *
 * NOT applied — and these are the residual channels, named rather than papered
 * over:
 *
 *   - `children` and `items` order. Media-then-metadata and metadata-then-media
 *     are two different lenses, and telling them apart is the pool's entire job;
 *     a canonical sort would collapse them into one genome. Sibling order is
 *     meaning the pool actually needs, so it stays.
 *   - `sort` key precedence. Precedence *is* what a sort list means. Repeated
 *     roles are dropped below, since a second key on an already-sorted role can
 *     never apply, which leaves precedence over distinct roles as the remainder.
 */
function canonicalOrder<T>(values: readonly T[]): T[] {
  const byKey = new Map<string, T>();
  for (const v of values) byKey.set(JSON.stringify(v), v);
  return [...byKey.keys()].sort().map((k) => byKey.get(k)!);
}

/**
 * Rebuild a query carrying only closed-vocabulary values and positional
 * indices. Nothing here can hold a string a user chose.
 *
 * **Every scalar NAMED, not copied.** Task `0127`: this file's opening
 * paragraph promises *"every scalar on a genome is NAMED from a closed set
 * (`fromClosedSet`), never copied out of the spec"*, and the whole of this
 * function was the exception — `role`, `op`, `direction`, `fn`, `via` and the
 * group-by roles all arrived by assignment. `project()` was deliberately
 * callable without schema validation — decision `0044` ended that — so those
 * were exactly the channel the promise says is closed; `assertClosedVocabulary`
 * caught them at the guard, and this closes them at the construction the
 * sentence describes. Naming from a closed set is `0044` §2's own example of a
 * projection invariant that STAYS: a valid layout spec is not by that fact safe
 * to publish.
 *
 * The difference is the message. A bad `op` now refuses with
 * `/query/filter/where/op: not a member of its closed set` at the moment it is
 * projected, rather than later as a fact about a vocabulary.
 */
function stripQuery(q: TQuery, path: string, leaks: string[]): unknown {
  const filter = (node: unknown, at: string, depth = 0): unknown => {
    /**
     * A filter tree is unvalidated input too, and it nests without a node cap.
     *
     * @Codex, P2 on #184: *"For an unvalidated collection query containing
     * about 5,000 nested `not` nodes, the complete spec remains below
     * `MAX_VALUES`, but this recursive call throws `RangeError: Maximum call
     * stack size exceeded` instead of the abstraction gate's
     * `AbstractionError`. This occurs before the completed genome reaches the
     * leak assertion, so hostile input can crash the pool producer despite
     * accepted decision 0039 requiring refusal at this boundary."*
     *
     * Measured, and the depth is not the one the node tree fails at — a
     * `{ not: … }` link is smaller, so it takes twice as many to exhaust the
     * stack and it is still under `MAX_VALUES`:
     *
     * ```
     * 2,500 nested `not`   refused    (AbstractionError)
     * 5,000 nested `not`   RangeError: Maximum call stack size exceeded
     * ```
     *
     * `MAX_GENOME_DEPTH` rather than `MAX_NODE_DEPTH`: this bound is about the
     * same nesting `assertClosedVocabulary` walks, and that guard already
     * refuses a filter deeper than 32 with *"nested deeper than 32, so nothing
     * below it could be checked"*. Projecting something the guard behind it
     * will refuse anyway is the same wasted pass `renumber` and `strip` share a
     * constant to avoid — and it means the two answers cannot disagree about
     * one tree.
     *
     * A `leaks` entry rather than a throw, because that is how everything else
     * in this function refuses: `project` collects and throws once, so a filter
     * too deep is reported beside whatever else was wrong with the spec instead
     * of hiding it.
     */
    if (depth > MAX_GENOME_DEPTH) {
      leaks.push(`${at}: nested deeper than ${MAX_GENOME_DEPTH}, so it cannot be projected`);
      return undefined;
    }
    if (!node || typeof node !== 'object') return undefined;
    const n = node as Record<string, unknown>;
    if (Array.isArray(n['all'])) {
      return { all: canonicalOrder(n['all'].map((c, i) => filter(c, `${at}/all/${i}`, depth + 1)).filter(Boolean)) };
    }
    if (Array.isArray(n['any'])) {
      return { any: canonicalOrder(n['any'].map((c, i) => filter(c, `${at}/any/${i}`, depth + 1)).filter(Boolean)) };
    }
    if (n['not']) return { not: filter(n['not'], `${at}/not`, depth + 1) };
    const w = n['where'] as { role: V.Role; op: V.Operator; valueFrom?: number } | undefined;
    if (!w) return undefined;
    return {
      where: {
        role: fromClosedSet(w.role, V.ROLES, `${at}/where/role`, leaks),
        op: fromClosedSet(w.op, V.OPERATORS, `${at}/where/op`, leaks),
        ...(w.valueFrom !== undefined ? { valueFrom: w.valueFrom } : {}),
      },
    };
  };

  // Precedence is preserved — it is what a sort list means — but a role already
  // sorted on cannot be sorted on again, so a repeat carries no result and only
  // widens the permutation a payload can hide in.
  const sortKeys = (keys: NonNullable<TQuery['sort']>) => {
    const seen = new Set<V.Role>();
    const out: Array<{ role: V.Role; direction: V.SortDirection }> = [];
    keys.forEach((k, i) => {
      if (seen.has(k.role)) return;
      seen.add(k.role);
      out.push({
        role: fromClosedSet(k.role, V.ROLES, `${path}/sort/${i}/role`, leaks),
        direction: fromClosedSet(
          k.direction, V.SORT_DIRECTIONS, `${path}/sort/${i}/direction`, leaks),
      });
    });
    return out;
  };

  return {
    ...(q.filter ? { filter: filter(q.filter, `${path}/filter`) } : {}),
    ...(q.sort ? { sort: sortKeys(q.sort) } : {}),
    ...(q.groupBy
      ? { groupBy: fromClosedSet(q.groupBy, V.ROLES, `${path}/groupBy`, leaks) } : {}),
    ...(q.subGroupBy
      ? { subGroupBy: fromClosedSet(q.subGroupBy, V.ROLES, `${path}/subGroupBy`, leaks) } : {}),
    ...(q.aggregate
      ? {
        aggregate: canonicalOrder(q.aggregate.map((a, i) => ({
          role: fromClosedSet(a.role, V.ROLES, `${path}/aggregate/${i}/role`, leaks),
          fn: fromClosedSet(a.fn, V.AGGREGATIONS, `${path}/aggregate/${i}/fn`, leaks),
        }))),
      }
      : {}),
    ...(q.traverse
      ? {
        traverse: {
          via: fromClosedSet(q.traverse.via, V.ROLES, `${path}/traverse/via`, leaks),
          depth: q.traverse.depth,
        },
      }
      : {}),
  };
}

/**
 * Placement IS poolable, and that is the point of decision 0014: "people who
 * work this way dock the panel to the inline end" is exactly the kind of taste
 * the pool exists to learn. What is not poolable — where the user dragged it,
 * how wide they made it, whether it is open right now — is view state and
 * cannot be expressed in a spec at all, so there is nothing here to filter.
 *
 * Reconstructed field by field, never copied. `query` was copied wholesale in
 * this same file and accounted for three separate confirmed leaks; a
 * passthrough also means the next field added to `Placement` leaks by default.
 */
function stripPlacement(
  placement: unknown, path: string, idMap: Map<string, string>, leaks: string[],
): unknown {
  if (!placement || typeof placement !== 'object' || Array.isArray(placement)) {
    // Dropped silently, this reads as an inline node — and `strip`'s `default:`
    // case argues the opposite for an unknown node kind two hundred lines down:
    // "a genome that quietly discards half a spec is not a refusal either."
    leaks.push(`${path}: placement is not an object`);
    return undefined;
  }
  const p = placement as Record<string, unknown>;
  const mode = fromClosedSet(p['mode'], V.PLACEMENT_MODES, `${path}/mode`, leaks);
  // `inline` is the default and carries no information, so it is dropped rather
  // than emitted: two specs that differ only by an explicit `inline` should hash
  // the same, or the pool learns a distinction that does not exist.
  if (mode === 'inline') return undefined;

  const extent = p['extent'] === undefined
    ? {}
    : { extent: fromClosedSet(p['extent'], V.EXTENTS, `${path}/extent`, leaks) };
  const collapsible = typeof p['collapsible'] === 'boolean'
    ? { collapsible: p['collapsible'] }
    : {};

  if (mode === 'docked') {
    return { mode, edge: fromClosedSet(p['edge'], V.DOCK_EDGES, `${path}/edge`, leaks),
             ...extent, ...collapsible };
  }
  if (mode === 'floating') {
    return { mode, rest: fromClosedSet(p['rest'], V.ATTACHMENTS, `${path}/rest`, leaks),
             ...extent, ...collapsible };
  }
  // anchored. The anchor is NOT carried, and the first version of this was
  // wrong about why.
  //
  // It carried the canonical index — `n80` rather than the author's id — on the
  // reasoning that a number cannot hold a name. An adversarial pass then wrote
  // "MORPH" into a genome as a sequence of anchor indices from a spec that
  // validates cleanly: `strip` emits no `id` on any node, so the index resolves
  // against nothing and is pure author-chosen number, roughly 7 bits per
  // anchored node with the count bounded only by the node cap. That is the
  // argument this file already makes for `ASPECTS` — digits carry a phone
  // number as well as letters do — arriving at the one field that had survived
  // it, and `assertNoLeak` is structurally blind to it because there is no
  // needle to search for.
  //
  // The poolable taste is "this panel attaches to another node, on that side".
  // WHICH node is layout detail of no use to anyone else, so the channel is
  // removed rather than narrowed.
  const raw = p['anchor'];
  if (typeof raw !== 'string' || !idMap.has(raw)) {
    leaks.push(`${path}/anchor: anchored to an id that is not a node in this spec`);
    return undefined;
  }
  return { mode,
           attachment: fromClosedSet(p['attachment'], V.ATTACHMENTS, `${path}/attachment`, leaks),
           ...extent, ...collapsible };
}

/**
 * An invocation, projected — and the ACTION NAME is deliberately not in it.
 *
 * The registry is open (decision 0010), so an action name is author-controlled
 * free text reaching a genome that gets serialized into another user's
 * design-compiler prompt. It is the same channel `renumber` closes for node
 * ids, and here there is nothing to preserve: what the pool should learn is
 * "this card has a button that writes a property", which is the DECLARED
 * EFFECTS — a closed set. Which action implements it is tenant-specific and of
 * no use to anyone else.
 *
 * Arguments carry their role and their view-state index, both already closed or
 * bounded, and never a value.
 */
function stripInvocation(inv: unknown, path: string, leaks: string[]): unknown {
  if (!inv || typeof inv !== 'object') return undefined;
  const v = inv as Record<string, unknown>;
  const raw = Array.isArray(v['effects']) ? v['effects'] : [];
  if (raw.length === 0) {
    // The gate runs standalone, so it cannot assume the validator required
    // these. An invocation that claims no effect is not a harmless button — it
    // is one whose contract nothing can check.
    leaks.push(`${path}/effects: an invocation must declare at least one effect`);
    return undefined;
  }
  // Named from the vocabulary's own order and de-duplicated, for the reason
  // `poolableAffordances` gives: order and repetition count over a small closed
  // set are both payload, never meaning.
  const effects = V.EFFECTS.filter((e) => raw.includes(e));
  if (effects.length !== new Set(raw).size) {
    leaks.push(`${path}/effects: not a member of its closed set`);
  }
  const args = Array.isArray(v['args'])
    ? v['args'].map((a, i) => {
        const o = (a ?? {}) as Record<string, unknown>;
        const hasRole = o['role'] !== undefined;
        const hasValueFrom = o['valueFrom'] !== undefined;
        if (hasRole === hasValueFrom) {
          leaks.push(`${path}/args/${i}: an argument must name exactly one source`);
          return {};
        }
        if (hasRole) {
          return {
            role: fromClosedSet(o['role'], V.ROLES, `${path}/args/${i}/role`, leaks),
          };
        }
        if (typeof o['valueFrom'] === 'number' && Number.isInteger(o['valueFrom'])
          && o['valueFrom'] >= 0 && o['valueFrom'] <= 7) {
          return { valueFrom: o['valueFrom'] };
        }
        leaks.push(`${path}/args/${i}/valueFrom: not an index into viewState`);
        return {};
      })
    : [];
  return { effects, ...(args.length ? { args } : {}) };
}

export function project(spec: TLayoutSpec): PoolGenome {
  /**
   * Validation FIRST, before anything reads a field — decision `0044`.
   *
   * `project()` is exported and decision `0039` makes it the hard abstraction
   * gate, so it is reachable with arbitrary runtime input. It used to defend
   * itself with a hand-written schema check per field, which is a second
   * definition of layout legality maintained beside the real one: task `0133`
   * found four fields where the two had already drifted, and `via: 'dense'`,
   * `sort: {}` and `aggregate: {}` each produced a `TypeError` or a partial
   * genome rather than a refusal.
   *
   * `0044`: *"a convention that every caller validates first would recreate
   * the present failure whenever a new caller appears."* So it happens here,
   * once, and `validateSpec()` becomes the single owner of what a legal layout
   * spec is. What stays below is the projector's own question — what is safe
   * to publish — which a valid spec does not answer.
   *
   * A refusal, not a leaked validator result and not an exception: the
   * problems are carried on the `AbstractionError`, code and path intact.
   */
  const legal = validateSpec(spec);
  if (!legal.valid) {
    /**
     * Code and path, and NOT the validator's message — which is a channel.
     *
     * The first version of this boundary carried `p.message` through, and the
     * F11 control caught it within the hour: a 27-digit capability version is
     * refused by the validator with *"registry has text-editing@0.1.0, spec
     * asks for 999999999.888888888.777777777"*, so the smuggled digits came
     * straight back out in the refusal. F11 exists because that field was a
     * covert numeric channel; routing the refusal through a layer that quotes
     * author input reopened it in the one place nobody looks, the error.
     *
     * `0044` asks for *"at least its code and path"* and no more, and that is
     * exactly the line: a code is ours, a path is structural, a message is
     * whatever the author wrote. The message is replaced rather than dropped
     * so the reason it is missing travels with it.
     */
    const ranked = rankProblems(legal.problems).map((p) => ({
      path: p.path, code: p.code, message: WITHHELD,
    }));
    throw new AbstractionError(
      ranked.map((p) => `${p.path || '/'}: ${p.code}`),
      ranked,
    );
  }

  const leaks: string[] = [];

  // The same budget the validator uses, from the same function. `strip`'s own
  // `nodeCount` stays, because it feeds the shape metadata rather than the
  // budget — but it is no longer a second opinion about how large a spec may be.
  if (countValues(spec, MAX_VALUES) > MAX_VALUES) {
    throw new AbstractionError([`spec exceeds ${MAX_VALUES} values`]);
  }

  // The gate runs standalone, so it cannot assume the validator already refused
  // an external reference. Defence in depth, not a duplicate.
  //
  // Scanned across the WHOLE spec rather than per node: the first attempt
  // checked each node's own string properties, and a URL sitting in
  // `bindings[0].slot` — an array one level down — walked straight past it. The
  // test caught that, which is the entire argument for writing it first.
  //
  // Iterative with a WeakSet, mirroring `preScan` in validate.ts, because this
  // file says twice that a crash is not a refusal and the recursive version
  // made a liar of it: a two-node cyclic spec threw RangeError instead of
  // AbstractionError. `strip`'s depth guard sits behind this, so it never got
  // the chance to refuse.
  const seen = new WeakSet<object>();
  const stack: { value: unknown; path: string }[] = [{ value: spec, path: '' }];
  while (stack.length) {
    const { value, path } = stack.pop()!;
    if (typeof value === 'string') {
      if (V.looksExternal(value)) leaks.push(`${path}: references an external resource`);
      continue;
    }
    if (!value || typeof value !== 'object') continue;
    if (seen.has(value)) {
      leaks.push(`${path}: the spec contains a cycle`);
      continue;
    }
    seen.add(value);
    if (Array.isArray(value)) {
      value.forEach((v, i) => stack.push({ value: v, path: `${path}/${i}` }));
    } else {
      for (const [k, v] of Object.entries(value)) stack.push({ value: v, path: `${path}/${k}` });
    }
  }
  if (leaks.length) throw new AbstractionError(leaks);

  const capabilities = new Set<string>();
  const roleKinds = new Set<V.RoleKind>();
  let nodeCount = 0;
  let maxDepth = 0;
  let writable = false;

  function strip(node: TNode, path: string, depth: number): unknown {
    // The gate is called standalone, so it cannot rely on validation having
    // bounded the input. Unbounded recursion here is a RangeError, not a
    // rejection, and a crash is not a refusal.
    if (depth > MAX_NODE_DEPTH || nodeCount > MAX_NODES) {
      throw new AbstractionError(['spec exceeds the depth or size the gate will process']);
    }
    nodeCount += 1;
    maxDepth = Math.max(maxDepth, depth);

    const noteBinding = (b: { role: V.Role; direction: string }) => {
      roleKinds.add(V.ROLE_KIND[b.role]);
      if (b.direction === 'read-write') writable = true;
    };

    const affordances = poolableAffordances((node as { affordances?: unknown }).affordances);

    const rawInvocation = (node as { invocation?: unknown }).invocation;
    const invocation = rawInvocation === undefined
      ? undefined
      : stripInvocation(rawInvocation, `${path}/invocation`, leaks);
    const invokes = invocation === undefined ? {} : { invocation };

    const rawPlacement = (node as { placement?: unknown }).placement;
    const selfId = (node as { id?: string }).id;
    if (rawPlacement && typeof rawPlacement === 'object'
        && (rawPlacement as { mode?: string }).mode === 'anchored') {
      // The id map holds CANONICAL ids by now, and so does this node, so the
      // comparison is between two renumbered values rather than the author's.
      const target = (rawPlacement as { anchor?: unknown }).anchor;
      if (typeof target === 'string' && idMap.get(target) === selfId) {
        leaks.push(`${path}/placement/anchor: a node cannot be anchored to itself`);
      }
    }
    const placement = rawPlacement === undefined
      ? undefined
      : stripPlacement(rawPlacement, `${path}/placement`, idMap, leaks);
    const placed = placement === undefined ? {} : { placement };

    // Ids have already been canonically renumbered, so this is a tripwire for a
    // future path that skips renumbering rather than a check on user input.
    const id = (node as { id?: string }).id;
    if (id !== undefined && !/^n[0-9]+$/.test(id)) {
      leaks.push(`${path}/id: not canonically renumbered`);
    }

    switch (node.kind) {
      case 'region':
        return {
          kind: 'region',
          flow: node.flow,
          ...placed,
          ...(node.labelRole ? { labelRole: node.labelRole } : {}),
          // The PURPOSE survives projection. @Codex: *"a Connections stack and
          // a structurally identical ordinary node-slot region therefore
          // collapse to the same `PoolGenome`, so pooled taste learning cannot
          // distinguish the presentation semantics this field was introduced to
          // carry."*
          //
          // Which is the same mistake the renderer had just been corrected for,
          // one layer over: the field exists BECAUSE structure does not carry
          // the meaning, so a projection that keeps only structure throws away
          // the only thing that does.
          //
          // TASK 0175 read this line's old comment — "A closed set of one
          // value, so it widens nothing a genome can say" — noticed that
          // `REGION_PURPOSES` has EIGHT members (relations, explorer, editor,
          // backlinks, properties, sidebar, graph, search), and filed it as a
          // leak: a spec's own word crossing the one door this file exists to
          // keep shut. The comment WAS false and is corrected here. **The leak
          // was not.**
          //
          // Decision 0044 made `project()` validate before it strips, so a
          // purpose outside the vocabulary is refused at the boundary with the
          // field named — measured, not reasoned: the clause in
          // `pool.test.ts` that smuggles `exfiltrate-me-9f3a` gets an
          // `AbstractionError` naming `/purpose`, and never reaches this line.
          //
          // So `fromClosedSet` was tried here and REVERTED, because nothing
          // could fail for it. Removing the call left all 93 clauses green;
          // making `fromClosedSet` itself permissive left all 93 green. A
          // hardening no control can distinguish from its absence is the shape
          // this repository already records from the other direction — a
          // hardening that makes the only control unable to fail. Both end the
          // same way: code that reads as protection and is not measured.
          //
          // What a genome may carry here is three bits from a closed set,
          // which is what the field is for, and the boundary is what keeps it
          // closed.
          ...(node.purpose ? { purpose: node.purpose } : {}),
          // Sibling order is NOT canonicalised: see `canonicalOrder`. It is a
          // residual channel we keep on purpose, because media-first and
          // metadata-first are different lenses and the pool has to tell them
          // apart.
          children: node.children.map((c, i) => strip(c, `${path}/children/${i}`, depth + 1)),
        };
      case 'collection':
        return {
          kind: 'collection',
          flow: node.flow,
          ...placed,
          ...invokes,
          // Reconstructed field by field, never copied. Every other branch here
          // is safe precisely because it enumerates what it emits; this one
          // copied an object wholesale, and that single difference accounted
          // for three separate confirmed leaks. A passthrough also means the
          // NEXT field added to Query leaks by default.
          ...(node.query ? { query: stripQuery(node.query, `${path}/query`, leaks) } : {}),
          // Carried on purpose. It is a closed-set word, so it cannot hold
          // anything of the tenant's — and it is a real structural fact the
          // taste model should learn from: a panel showing SIBLINGS beside
          // content showing the SUBJECT is a different layout from two
          // collections of the same thing, and a genome that could not tell
          // them apart would rank them as if they were interchangeable.
          // NAMED, never copied. The first version of this was a passthrough,
          // written four lines under the comment above explaining that a
          // passthrough is exactly what caused three separate confirmed leaks
          // here. `project()` ran on specs the validator never saw — decision
          // `0044` ended that — so `source: "tenant-secret"` went straight into
          // the shared genome.
          // Found by @Codex.
          // Checked always, emitted only when it says something. `subject` IS
          // absence — the schema defines it that way, the compiler normalizes
          // it and the renderer renders them identically — so emitting it here
          // split one layout into two genomes and fragmented the taste signal
          // between them, letting a serialization detail influence ranking.
          // Found by @Codex, one round after the same equivalence was fixed in
          // the compiler and the renderer and not here.
          //
          // The membership check runs first regardless: a bad value must be
          // recorded as a leak, never quietly normalized away.
          ...(node.source !== undefined
            && fromClosedSet(node.source, V.SOURCES, `${path}/source`, leaks) !== 'subject'
            ? { source: node.source }
            : {}),
          /**
           * Item order is NOT canonicalised, for the same reason as
           * `children`: a polymorphic collection tries its item specs in
           * order, so the order is the rule. Residual channel, named in
           * `canonicalOrder`.
           *
           * And the GUARD travels with each item, which it did not.
           *
           * @Codex, P2 on `41e9da0`: *"When two valid accepted collections
           * contain the same ordered item templates but route them with
           * different `when.has` roles, this projection emits only each item's
           * `spec`, so both layouts become the same genome and
           * `PrivateGenomeStore` deduplicates the second contribution under
           * the same content ID. The runtime actually uses `when.has` to
           * select a template per object, so these arrangements behave
           * differently even though the pool records them as identical."*
           *
           * The order was carried precisely because *"the order is the rule"*,
           * and the guard is the other half of that rule — an ordered list of
           * templates with no conditions is a list where only the first can
           * ever match. Two arrangements that draw different objects
           * differently were one genome, and `0039` asks the projection to be
           * a canonical account of the structure.
           *
           * A `RoleRef` through the closed set, like every other role in this
           * file: the guard names a role and a role is vocabulary, so an
           * author-chosen string cannot ride in on it.
           *
           * This CHANGES the genome shape — items are `{ when?, spec }` rather
           * than bare specs — so genomes contributed before it no longer match
           * ones projected after. That is the same cost the derivative probe
           * pays for the same reason: a key that did not identify what it
           * names was not worth preserving.
           */
          items: node.items.map((it, i) => ({
            ...(it.when
              ? { when: { has: fromClosedSet(it.when.has, V.ROLES, `${path}/items/${i}/when/has`, leaks) } }
              : {}),
            spec: strip(it.spec, `${path}/items/${i}`, depth + 1),
          })),
          ...(affordances.length ? { affordances } : {}),
        };
      case 'card':
        node.bindings.forEach(noteBinding);
        return {
          kind: 'card',
          ...placed,
          ...invokes,
          bindings: canonicalOrder(node.bindings.map((b) => ({
            // `slot` is author-chosen and deliberately NOT carried: the runtime
            // resolves slots through the private binding manifest, so the genome
            // has no use for the name and every reason not to carry it.
            role: fromClosedSet(b.role, V.ROLES, `${path}/bindings/role`, leaks),
            direction: fromClosedSet(
              b.direction, V.BINDING_DIRECTIONS, `${path}/bindings/direction`, leaks),
            ...(b.mutation
              ? { mutation: fromClosedSet(
                b.mutation, V.MUTATIONS, `${path}/bindings/mutation`, leaks) }
              : {}),
          }))),
          // Sibling order stays: see `canonicalOrder` — it is a residual channel
          // the pool needs, not one we forgot.
          ...(node.children
            ? { children: node.children.map((c, i) => strip(c, `${path}/children/${i}`, depth + 1)) }
            : {}),
          ...(affordances.length ? { affordances } : {}),
        };
      case 'field':
        noteBinding(node.binding);
        return {
          kind: 'field',
          role: fromClosedSet(node.binding.role, V.ROLES, `${path}/role`, leaks),
          direction: fromClosedSet(
            node.binding.direction, V.BINDING_DIRECTIONS, `${path}/direction`, leaks),
        };
      case 'media':
        noteBinding(node.binding);
        // `optional` is carried: a boolean from the closed schema that cannot
        // hold anything of the tenant's, and a real structural fact — a card
        // whose media may be absent is a different layout from one whose media
        // is guaranteed, and the taste model should be able to tell them apart.
        return {
          kind: 'media',
          role: fromClosedSet(node.binding.role, V.ROLES, `${path}/role`, leaks),
          fit: fromClosedSet(node.fit, V.FITS, `${path}/fit`, leaks),
          ...(node.aspect
            ? { aspect: fromClosedSet(node.aspect, V.ASPECTS, `${path}/aspect`, leaks) }
            : {}),
          ...(node.optional === true ? { optional: true } : {}),
        };
      case 'node-slot':
        roleKinds.add(V.ROLE_KIND[node.via]);
        /**
         * The DECLARED mutations travel, as a SET: sorted and deduplicated.
         *
         * They are two closed words from the vocabulary and carry nothing of
         * the tenant's, and "this relation could be attached to" is a real
         * structural difference the taste model should be able to see — a
         * read-only reference and an authored one are different layouts.
         *
         * Sorted because a genome that differs only in the order two words were
         * written is the same layout wearing two fingerprints. @Codex, P2 on
         * #184: the same sentence, and repetition was the half it did not
         * cover — *"a node slot with `mutations: ['link']` and one with
         * `mutations: ['link', 'link']` both validate and grant exactly the
         * same authority, but array canonicalization preserves the duplicate,
         * so `genomeId()` assigns them different identities. Repetition
         * therefore fragments one structural layout into separate genomes and
         * can carry an otherwise meaningless bit into the pool."*
         *
         * `schema.ts` calls this *"which relation mutations a person may
         * perform here"* — an authority, which a value either has or does not.
         * Its `maxItems: 2` over a two-word vocabulary is what lets the
         * duplicate through validation: `['link', 'link']` is two items from
         * the enum and is the same permission stated twice.
         *
         * Deduplicated HERE rather than in `genomeId`'s canonicalisation, and
         * the difference matters. The projection is where a spec becomes the
         * thing the pool holds, so a genome is identical to another genome
         * whose spec said the same thing twice — not merely hashed to the same
         * number. A fix in the hash would leave two different genomes agreeing
         * on their id, which is a worse property than the one it repaired.
         */
        return { kind: 'node-slot', via: node.via, maxDepth: node.maxDepth,
                 ...(node.mutations?.length
                   ? { mutations: [...new Set(node.mutations)].sort() }
                   : {}) };
      case 'capability': {
        // Resolved from the registry rather than copied, and that does not
        // change under decision `0044`: the validator says a capability name is
        // well formed, and this says it is a module we actually have. A
        // syntactically valid author string is not a module; only a registry hit
        // may reach a genome. `0044` §2 — the two checks compose, neither
        // substitutes for the other.
        const module = lookupModule(node.capability);
        if (module) capabilities.add(module.id);

        // Every config key is rebuilt against the descriptor-owned shape. The
        // own-property check remains essential even though the descriptor is
        // snapshotted: constructor and caller are legal schema keys and must not
        // reach through Object.prototype.
        const shapes = module?.configShapes ?? {};
        const config: Record<string, unknown> = {};
        // Config is a record, so authored insertion order has no meaning and
        // must not become a permutation channel in the serialized genome.
        const authoredConfig = node.config ?? {};
        for (const k of Object.keys(authoredConfig).sort()) {
          const v = authoredConfig[k];
          const shape = Object.prototype.hasOwnProperty.call(shapes, k)
            ? shapes[k] : undefined;
          if (!shape) { leaks.push(`${path}/config/${k}: key not poolable`); continue; }
          const ok =
            (shape.type === 'boolean' && typeof v === 'boolean') ||
            (shape.type === 'integer' &&
              typeof v === 'number' && Number.isInteger(v) && v >= shape.min && v <= shape.max) ||
            (shape.type === 'enum' && typeof v === 'string' && shape.values.includes(v));
          if (!ok) { leaks.push(`${path}/config/${k}: value fails its declared shape`); continue; }
          config[k] = v;
        }

        const channels: Record<string, unknown> = {};
        const allowedChannels = module?.channels ?? [];
        const rawChannels = (node as { channels?: unknown }).channels;
        const supplied = rawChannels === undefined
          ? {}
          : rawChannels !== null && typeof rawChannels === 'object' && !Array.isArray(rawChannels)
            ? rawChannels as Record<string, unknown>
            : (leaks.push(`${path}/channels: channels must be an object`), {});
        for (const name of Object.keys(supplied)) {
          if (!allowedChannels.includes(name)) {
            leaks.push(`${path}/channels/${name}: channel name not in the capability's closed set`);
          }
        }
        // Descriptor order is canonical. A channel record is orderless, so
        // author insertion order must not survive into a pooled genome.
        for (const name of allowedChannels) {
          const raw = supplied[name];
          if (raw === undefined) continue;
          if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
            leaks.push(`${path}/channels/${name}: binding must be an object`);
            continue;
          }
          const b = raw as Record<string, unknown>;
          const role = b['role'];
          const direction = b['direction'];
          const mutation = b['mutation'];
          if (typeof role !== 'string' || !(V.ROLES as readonly string[]).includes(role)) {
            leaks.push(`${path}/channels/${name}/role: role not in the closed set`);
            continue;
          }
          const admittedRoles = Object.prototype.hasOwnProperty.call(module?.channelRoles ?? {}, name)
            ? module!.channelRoles[name] : undefined;
          const admittedKinds =
            Object.prototype.hasOwnProperty.call(module?.channelRoleKinds ?? {}, name)
              ? module!.channelRoleKinds[name] : undefined;
          if ((admittedRoles !== undefined && !admittedRoles.includes(role as V.Role))
              || (admittedKinds !== undefined
                && !admittedKinds.includes(V.ROLE_KIND[role as V.Role]))) {
            leaks.push(`${path}/channels/${name}/role: role not admitted by channel`);
            continue;
          }
          if (direction !== 'read' && direction !== 'read-write') {
            leaks.push(`${path}/channels/${name}/direction: direction not in the closed set`);
            continue;
          }
          /**
           * The direction/mutation CONSISTENCY pair is gone — `0044` §1.
           *
           * A read binding declaring a mutation, and a writable one declaring
           * none, are decision `0007`'s rule about layout-spec legality, and
           * `validateSpec()` owns it: `mutation-without-write` and
           * `write-without-mutation`, both at the binding's own path. The
           * control is `force-graph-channels.test.ts`'s *"refuses a writable
           * channel that names no mutation, and a read one that does"*, which
           * asserts those two codes through the boundary.
           *
           * What stays is MEMBERSHIP, which is not the same question: `0044`
           * §2 keeps closed-vocabulary membership here because a valid layout
           * spec is not by that fact safe to publish. So a mutation that is
           * present must be in the closed set, whatever its direction — and
           * whether it should be present at all is now asked once, upstream.
           */
          // `typeof` inside the membership test, not beside it: a mutation that
          // is present but is not a string is not a member either, and dropping
          // the narrowing to shorten the condition made it a type error rather
          // than a silent hole — which is the compiler doing the audit's job.
          if (mutation !== undefined
              && (typeof mutation !== 'string'
                || !(V.MUTATIONS as readonly string[]).includes(mutation))) {
            leaks.push(`${path}/channels/${name}/mutation: mutation not in the closed set`);
            continue;
          }
          const safe = {
            role: role as V.Role,
            direction,
            ...(typeof mutation === 'string' ? { mutation } : {}),
          };
          noteBinding(safe);
          channels[name] = safe;
        }

        /**
         * Slots in DESCRIPTOR order, exactly as channels and config already are.
         *
         * @Codex, P2 on #184: *"semantically identical accepted specs can
         * supply the same slot mapping in different object insertion orders,
         * and this `Object.entries()` walk preserves that authored ordering in
         * the projected genome. Although `genomeId()` later sorts object keys,
         * the raw genome itself is stored and snapshotted, so
         * `Object.keys(structure.slots)` still exposes whichever permutation
         * arrived first; this carries authored bits across the abstraction
         * gate."*
         *
         * Right, and the tell is that this was the only one of the three
         * records left walking the author's object: `config` sorts its keys
         * and `channels` iterates `module.channels`, each with a comment
         * saying why. Decision `0039` asks the projected structure ITSELF to
         * be canonical, and `genomeId()` sorting downstream is a different
         * claim — the genome is what gets stored, snapshotted and read back.
         *
         * A name outside the closed set leaks and is DROPPED rather than
         * projected, which is what the channels loop above does with an
         * unrecognised channel: a leak refuses the projection, so what an
         * illegal name projects to cannot matter, and it must not be a way to
         * put an authored key into the output.
         */
        const projectedSlots = (
          authored: Record<string, readonly unknown[]>,
        ): Record<string, unknown[]> => {
          const declared = module?.slots ?? [];
          for (const slot of Object.keys(authored)) {
            if (!declared.includes(slot)) {
              leaks.push(`${path}/slots: slot name not in the capability's closed set`);
            }
          }
          const out: Record<string, unknown[]> = {};
          for (const slot of declared) {
            const kids = authored[slot];
            if (kids === undefined) continue;
            out[slot] = kids.map((child, i) =>
              strip(child as never, `${path}/slots/${slot}/${i}`, depth + 1));
          }
          return out;
        };

        // The registry supplies the one trusted version and identity. Copying
        // authored values here would reopen the numeric and string channels the
        // abstraction gate is specifically responsible for closing.
        const registered = module?.version;
        if (registered === undefined) {
          leaks.push(`${path}/capability: not a registered capability`);
        } else if (node.version !== registered) {
          leaks.push(`${path}/version: not the version the registry holds for this capability`);
        }
        return {
          kind: 'capability',
          ...placed,
          ...(module ? { capability: module.id, version: module.version } : {}),
          ...(Object.keys(config).length ? { config } : {}),
          ...(Object.keys(channels).length ? { channels } : {}),
          ...(node.slots ? { slots: projectedSlots(node.slots) } : {}),
        };
      }

      default:
        // validate.ts says unknown constructs FAIL CLOSED. This function is the
        // one documented as running WITHOUT the validator, and it had no
        // default — so an unrecognised kind produced a genome whose `structure`
        // was `undefined`, with every subtree beneath it silently gone and no
        // leak recorded. Nothing extra rode in, but a genome that quietly
        // discards half a spec is not a refusal either.
        leaks.push(`${path}/kind: unknown node kind`);
        return undefined;
    }
  }

  // Renumber BEFORE stripping, so no author-chosen id can reach the genome.
  // The gate runs standalone, so the three placement rules the validator
  // enforces have to hold here too. An adversarial pass got a clean genome out
  // of all three: a placed root, a self-anchor, and duplicate ids where the
  // anchor silently resolved to whichever copy came last.
  const rootPlace = (spec.root as { placement?: { mode?: string } } | undefined)?.placement;
  if (rootPlace && rootPlace.mode !== 'inline') {
    leaks.push('/root/placement: the root is the surface and cannot be placed within itself');
  }

  const idMap = new Map<string, string>();
  const canonical = renumber(spec.root, { n: 0 }, idMap);
  const structure = strip(canonical, '/root', 0);

  for (const slot of spec.viewState ?? []) {
    if (isFreeText(slot.name)) {
      leaks.push('/viewState: slot name reads as free text');
    }
  }

  // `target: spec.target` was a wholesale copy of an author-controlled object,
  // inside the one function whose stated rule is "reconstructed field by field,
  // never copied". Without validation `additionalProperties: false` never runs,
  // so an extra key rode along; and an unchecked VALUE is exactly as wide a
  // channel as an unchecked key, which is why each field is named from its set
  // rather than merely re-listed. `?? {}` because a missing target has to be a
  // refusal, not a TypeError — a crash is not a refusal.
  const target = (spec.target ?? {}) as Partial<TLayoutSpec['target']>;

  /**
   * THE PARTITION, renumbered like an anchored placement and for the same
   * reason.
   *
   * @Codex's requirement 7 on the 0020 ruling: *"Pool validation and
   * sanitization must preserve this declaration as structural vocabulary while
   * continuing to exclude the slot's private value."*
   *
   * Both halves are load-bearing here, and the first one is a trap. `branches`
   * holds AUTHORED NODE IDS — free strings a person chose — so copying them
   * into a shared genome would leak exactly what `renumber` exists to erase.
   * They are translated through `idMap`, which is the same treatment
   * `stripPlacement` gives an anchor, and an id the map does not know is a leak
   * rather than a passthrough.
   *
   * The value half needs nothing: `valueFrom` is an index into `viewState`, so
   * what crosses is a small integer bounded by the slot count. No threshold, no
   * content, nothing derived from what is in the pool.
   */
  const rawPartition = (spec as { partition?: V.TPartitionShape }).partition;
  const canonicalBranch = (id: unknown, where: string): string => {
    if (typeof id !== 'string' || !idMap.has(id)) {
      leaks.push(`${where}: names a node this spec does not contain`);
      return '';
    }
    return idMap.get(id)!;
  };
  const partition = rawPartition === undefined ? undefined : {
    valueFrom: Number.isInteger(rawPartition.valueFrom)
      && rawPartition.valueFrom >= 0
      && rawPartition.valueFrom < (spec.viewState?.length ?? 0)
      ? rawPartition.valueFrom
      : (leaks.push('/partition/valueFrom: not an index into viewState'), 0),
    /**
     * SORTED, because the list is membership and nothing reads its order.
     *
     * @Codex, P2 on `dcc6189`: *"When two accepted specs name the same
     * partition branches in different array orders, runtime treats the field
     * only as membership and validation imposes no semantic order, but this
     * projection preserves the authored permutation after renumbering. The
     * behaviorally identical specs consequently receive different genomes and
     * `genomeId`s, so repeated permutations can enter the private pool as
     * independent votes."*
     *
     * Checked rather than taken on trust: `membershipQuery.ts` is the only
     * reader and it does `branches.filter((b) => b !== unset.show)` — a set
     * operation. The schema says *"node ids of the regions this control
     * splits"*, and says nothing about which comes first.
     *
     * The independent-votes half is the sharper one. `0040` weights the pool
     * by distinct genome, and a permutation is a way to make one arrangement
     * count twice — which is the same door `distinctVotes` has now had closed
     * three times from the scoring side, standing open on the projection side.
     *
     * Sorted AFTER renumbering, so the order is a function of the canonical
     * ids rather than of the authored ones. Sorting the authored ids would
     * reproduce the permutation under another name.
     *
     * `unset.show` is untouched: it is a single id and a different field, and
     * a branch appearing in both is exactly how a partition says "this region
     * is also the empty state".
     */
    branches: Array.isArray(rawPartition.branches)
      ? rawPartition.branches
        .map((b, i) => canonicalBranch(b, `/partition/branches/${i}`))
        .sort()
      : (leaks.push('/partition/branches: branches must be an array'), []),
    unset: {
      show: canonicalBranch(
        (rawPartition.unset ?? {}).show, '/partition/unset/show'),
    },
  };

  const genome: PoolGenome = {
    vocabularyVersion:
      fromClosedSet(spec.vocabularyVersion, [V.VOCABULARY_VERSION], '/vocabularyVersion', leaks),
    target: {
      cardinality: fromClosedSet(target.cardinality, V.CARDINALITIES, '/target/cardinality', leaks),
      mediaWeight: fromClosedSet(target.mediaWeight, V.MEDIA_WEIGHTS, '/target/mediaWeight', leaks),
      context: fromClosedSet(target.context, V.USE_CONTEXTS, '/target/context', leaks),
    },
    density: fromClosedSet(spec.density, V.DENSITIES, '/density', leaks),
    typography: fromClosedSet(spec.typography, V.TYPE_SYSTEMS, '/typography', leaks),
    palette: fromClosedSet(spec.palette, V.PALETTES, '/palette', leaks),
    structure,
    ...(partition ? { partition } : {}),
    ...(spec.template
      ? { template: fromClosedSet(spec.template.id, V.APP_TEMPLATES, '/template/id', leaks) }
      : {}),
    shape: {
      depth: bucket(maxDepth, DEPTH_BUCKETS),
      nodeCount: bucket(nodeCount, NODE_BUCKETS),
      capabilities: [...capabilities].sort(),
      roleKinds: [...roleKinds].sort(),
      writable,
    },
  };

  // The manifest reference is a private, tenant-scoped identifier, and its
  // absence is the invariant the whole design rests on.
  //
  // This checked `'bindingManifestRef' in structure` — the STRIPPED ROOT NODE,
  // which is rebuilt key by key from a closed set and never had the property.
  // `bindingManifestRef` is a property of the SPEC, so no input could set the
  // guard off; it read as the invariant being enforced while enforcing nothing,
  // which is worse than no guard at all. Search the finished genome for the
  // VALUE instead — that is the fact we care about, it fires, and a false
  // positive refuses a pooling, which is the safe direction.
  if (spec.bindingManifestRef && JSON.stringify(genome).includes(spec.bindingManifestRef)) {
    leaks.push('/root: binding manifest reference survived projection');
  }

  if (leaks.length) throw new AbstractionError(leaks);

  return genome;
}

/* ────────────────────────────────────────────── the closed vocabulary */

/**
 * Every string a genome is allowed to contain.
 *
 * Decision `0039` calls a genome a *"closed-vocabulary projection"*. Until task
 * `0124` nothing implemented that sentence as a check — `assertNoLeak` asked
 * the OPPOSITE question, *"does the genome contain something private?"*, over
 * an open-ended list of the host's own strings. That has both failure modes at
 * once:
 *
 * - it **refuses what it should accept**. The needles are content, the genome
 *   is vocabulary, and the two collide: a person with a note called `Dense fog`,
 *   an author named `Neutral` or a tag `title` could never contribute to their
 *   own pool, because `dense`, `neutral` and `title` are vocabulary terms and
 *   the search is by substring;
 * - it **accepts what it never looks at**. A needle nobody thought to list is
 *   a string nobody checks, and the list is assembled by the host from the
 *   rows it happens to be holding.
 *
 * Membership is strictly stronger than any needle list: it catches content
 * nobody enumerated, at any depth, of any length — and it makes the
 * false-positive class disappear, because a private value that coincides with
 * a vocabulary term is no longer evidence of anything.
 *
 * Built lazily rather than at module load: `registeredModuleIds()` grows as
 * capabilities register, and a set frozen at import time would refuse a module
 * loaded afterwards. Cheap either way — this runs once per Keep, over a genome
 * of a few hundred bytes.
 */
function closedVocabulary(): ReadonlySet<string> {
  return new Set<string>([
    V.VOCABULARY_VERSION,
    ...V.CARDINALITIES, ...V.MEDIA_WEIGHTS, ...V.USE_CONTEXTS,
    ...V.DENSITIES, ...V.TYPE_SYSTEMS, ...V.PALETTES,
    ...V.PLACEMENT_MODES, ...V.EXTENTS, ...V.DOCK_EDGES, ...V.ATTACHMENTS,
    ...V.ROLES, ...V.SOURCES, ...V.AFFORDANCES, ...V.EFFECTS, ...V.MUTATIONS,
    ...V.ROLE_KINDS, ...Object.values(V.ROLE_KIND),
    ...V.PROSE_ROLES, ...V.RELATION_ROLES, ...V.RELATION_MUTATIONS,
    ...V.BINDING_SITES, ...V.CAPABILITY_IDS, ...V.FOCUS_POLICIES,
    /**
     * The five families the first version of this list forgot, and finding
     * them is what clause 3 of `0124` is for: a battery of two real
     * projections reported seventeen strings and keys outside the enumeration,
     * against a list that read as complete.
     *
     * Worth naming what they revealed. Every one of these reaches a genome by
     * being COPIED out of the spec rather than named through `fromClosedSet` —
     * `node.fit`, `node.aspect`, `w.op`, `k.direction`, `a.fn` and the `role`
     * on every query clause. This file's own opening comment says *"every
     * scalar on a genome is NAMED from a closed set … never copied out of the
     * spec"*, and `project()` was deliberately callable without schema
     * validation when that was written, so those six are exactly the channel
     * the sentence promises is closed. Decision `0044` put the validator in
     * front of them; naming from a closed set stays regardless, because it is
     * about what may be published rather than about what is legal. Membership closes it at the guard; converting the six call
     * sites is the deeper fix and is recorded in `0124`.
     */
    ...V.FLOWS, ...V.ASPECTS, ...V.OPERATORS, ...V.AGGREGATIONS, ...V.DERIVATIONS,
    'cover', 'contain',            // `fit`, a schema literal with no constant
    'asc', 'desc',                 // a sort key's direction
    'read', 'read-write',          // a binding's direction
    // The `kind` discriminators `strip` writes. Literals in this file rather
    // than a vocabulary constant, which is exactly why they have to be listed
    // here by hand — and why clause 3 of `0124` is a battery of real
    // projections rather than a reading of this list.
    'card', 'collection', 'field', 'media', 'region', 'node-slot', 'capability',
    // A region's declared purpose, and the named app looks — both closed
    // sets a genome carries as words (decision 0055).
    ...V.REGION_PURPOSES, ...V.APP_TEMPLATES,
    /**
     * Everything a REGISTERED capability contributes — the descriptor's WHOLE
     * string surface, not the three fields somebody has needed so far.
     *
     * All of it is registry-owned rather than author-supplied: `strip` calls
     * `lookupModule(node.capability)` and admits only a registry hit, then
     * rebuilds `config` against that descriptor's own `configShapes`.
     * Registration validates every one of these names against the same syntax
     * `schema.ts` enforces, so none of them can be a person's value unless a
     * module registers a descriptor built out of one — which would be a defect
     * in that module rather than a hole here.
     *
     * Enumerated from the registry rather than listed, so a capability
     * registered at runtime does not have to be added here — which is the
     * whole reason `closedVocabulary()` is a function and not a constant.
     */
    ...registeredModuleIds().flatMap((id) => {
      const module = lookupModule(id);
      return module ? [...descriptorVocabulary(module)] : [id];
    }),
  ]);
}

/**
 * Every string a module descriptor OWNS, in one place. Task `0134`.
 *
 * @Codex, on #184: *"When a runtime-registered module declares a novel valid
 * slot or channel name such as `custom-slot`, `project()` accepts and preserves
 * that descriptor-owned key, but this registry expansion adds only the module
 * id, version, and config vocabulary. `assertNoLeak()` consequently rejects
 * every accepted lens using that slot or channel as outside the closed
 * vocabulary, so Keep cannot contribute the module to the pool even though the
 * open-registry contract specifically requires runtime modules to be
 * sanitizable without editing a core table."*
 *
 * `0134` filed it rather than patching it, because it was the THIRD instance of
 * one shape and the first two had each been fixed by adding a name to a list:
 *
 * ```
 * purpose / relations      a generated region's own words → STRUCTURAL_KEYS
 * capability config keys   rebuilt from configShapes
 * module slots / channels  this one
 * ```
 *
 * So this derives the whole surface instead: a fourth descriptor-owned name
 * arrives here by being a field of `ModuleDescriptor`, not by somebody noticing
 * a genome being refused. The two `Record` fields contribute their KEYS, which
 * are channel names; their values are role kinds and roles, already in the
 * vocabulary above by their own constants rather than by way of a module.
 *
 * Refusing was the safe direction — nothing leaked, a contribution was silently
 * dropped — which is why this could wait for the derivation rather than needing
 * the fourth quick fix.
 */
function descriptorVocabulary(module: ModuleDescriptor): ReadonlySet<string> {
  return new Set<string>([
    module.id,
    module.version,
    module.fragmentation,
    ...module.slots,
    ...module.channels,
    ...Object.keys(module.channelRoleKinds),
    ...Object.keys(module.channelRoles),
    ...Object.keys(module.configShapes),
    ...Object.values(module.configShapes).flatMap((shape) =>
      (shape as { values?: readonly string[] }).values ?? []),
  ]);
}

/** Canonically renumbered node ids. `renumber` mints exactly `n<pre-order>`,
 *  and an id that does not match this shape is one that skipped it. */
const CANONICAL_ID = /^n\d+$/;

/**
 * The only place in a genome where a canonical node id may appear.
 *
 * @Codex, P2 on #184: *"this path-insensitive exemption accepts any `n<digits>`
 * string anywhere in the value. A forged structure such as `{ kind: 'field',
 * role: 'n123456' }` is therefore reported as closed vocabulary even though the
 * role is neither a Morph role nor a canonical node-ID position; because
 * `assertNoLeak()` returns immediately when `outside` is empty, it also skips
 * private needles matching that value."*
 *
 * The second sentence is the sharp end: the exemption did not merely admit a
 * junk role, it made the leak guard behind it stop looking. An exemption that
 * suppresses a later check has to be exactly as wide as the thing it exempts.
 *
 * ## The set is `canonicalBranch`'s call sites, and I measured the wrong thing
 *
 * The first version of this admitted `/partition/branches/<n>` only, from
 * walking a projected reference sheet for `n<digits>` strings and finding none
 * at all — `strip` drops every node `id` and `stripPlacement` removes the
 * anchor outright, *"WHICH node is layout detail of no use to anyone else, so
 * the channel is removed rather than narrowed"* — and then reading the source
 * for the rest.
 *
 * **The reference sheet has no partition.** So the measurement was of an
 * artifact that could not contain the thing being measured, and the missing
 * path turned up in a browser instead:
 *
 * ```
 * e2e/pool-gesture.mjs   outside: ["/partition/unset/show: \"n7\""]   6/9
 * ```
 *
 * The complete set is not "wherever ids survive" — it is **the call sites of
 * `canonicalBranch`**, which is the one function that mints a canonical id into
 * a genome by looking an authored id up in `idMap`. There are two, and a third
 * would have to go through it too:
 *
 * ```
 * /partition/branches/<n>     one per branch
 * /partition/unset/show       the branch shown when the value is unset
 * ```
 *
 * A genome carrying a canonical id anywhere else is carrying something nothing
 * in this file put there.
 */
const NODE_ID_PATH = /^\/partition\/(branches\/\d+|unset\/show)$/;
const bearsNodeId = (path: string): boolean => NODE_ID_PATH.test(path);

/**
 * The only paths from which an exempt path is reachable.
 *
 * `NODE_ID_PATH` matches two exact shapes, so a subtree can contain an exempt
 * leaf only if its own path is one of their prefixes. Everywhere else,
 * validating a value gives the same answer whatever route reached it — which
 * is what makes the `done` cache in `assertNoLeak` sound, and what made it
 * UNSOUND when it did not ask.
 */
const CANONICAL_PREFIXES: ReadonlySet<string> = new Set([
  '', '/partition', '/partition/branches', '/partition/unset',
]);

/**
 * Could the answer for this value depend on the path that reached it?
 *
 * @Codex, P2 on `8e4315a`: *"this global `done` cache skips validation at every
 * path after the first even though validation is path-dependent. For example,
 * the same `['n7']` array can appear first at `partition.branches`, where the
 * canonical ID is allowed, and later at `capabilities`, where it is not; the
 * second occurrence is skipped, `assertClosedVocabulary()` returns clean, and
 * `assertNoLeak()` accepts the forged genome."*
 *
 * Exactly right, and the cache's own comment stated the assumption it broke:
 * *"values already walked to completion by another route, whose violations are
 * therefore already recorded"* — true only if validation is path-independent,
 * and the canonical-id exemption is the one thing that is not.
 *
 * Dropping the cache was the other remedy offered and it is not affordable
 * here: `MAX_GENOME_DEPTH` is 32, so a shared subtree in a diamond could be
 * walked 2^32 times, which is the denial of service the cache was added to
 * prevent. So it is narrowed instead — the four prefixes above are the whole
 * of where the answer can vary, they are shallow, and there are four of them.
 */
const pathDecidesValidity = (path: string): boolean =>
  CANONICAL_PREFIXES.has(path) || bearsNodeId(path);

/** Note that `value` has been fully walked under this parent key. */
const remember = (done: Map<object, Set<string>>, value: object, under: string): void => {
  const seen = done.get(value);
  if (seen) seen.add(under);
  else done.set(value, new Set([under]));
};

/**
 * The domain of every number a genome may carry, by the key it sits under.
 *
 * @Codex, P2 on #184, after the generic bound: *"the replacement only rejects
 * numbers outside the generic range 0-4096 … for example, `maxDepth: 4096`
 * passes this check even though the schema permits only 1-4. An unvalidated
 * spec can therefore encode noncanonical state in a genome that
 * `assertNoLeak()` accepts."*
 *
 * They are right and `0127` recorded the opposite — that a range was enough for
 * two integers and naming them properly was not worth reopening. It named the
 * wrong reason: the work is small, the domains are in `schema.ts` already, and
 * *"a range rather than a domain"* was exactly the objection. `maxDepth` is
 * 1-4 and 4,096 is twelve bits of somewhere.
 *
 * Keyed by field name because that is what the walker has. A number under a key
 * nobody listed falls back to the bucket ladders, which are the only other
 * numbers a genome carries.
 */
/** What one numeric field may hold: an exact set, or an inclusive range. */
type NumericDomain =
  | { readonly set: readonly number[] }
  | { readonly range: readonly [number, number] };

/**
 * Keyed by the last TWO path segments where the last one alone is ambiguous.
 *
 * @Codex, P2 on #184: *"`depth` conflates two incompatible fields:
 * `/shape/depth` must be one of `DEPTH_BUCKETS` while `/traverse/depth` is
 * restricted to 1–4, yet both accept any integer through 4096; similarly,
 * `nodeCount` accepts values outside `NODE_BUCKETS`. An unvalidated genome can
 * therefore carry noncanonical payload such as traversal `depth: 4096` while
 * `assertClosedVocabulary()` reports it safe."*
 *
 * The previous table was keyed by the last segment alone and it said so out
 * loud — `depth: [0, 4096], // also shape.depth, a DEPTH_BUCKETS value`. Two
 * fields sharing a name got the union of their domains, which is the loosest
 * thing this table could have done and was written as a note rather than as a
 * problem.
 *
 * **And the reason the old comment gave for ranges does not survive reading
 * it.** It said a list of exact permitted values *"would be a second copy of
 * `vocabulary.ts` that drifts"*. `DEPTH_BUCKETS` and `NODE_BUCKETS` are
 * declared at the top of THIS file and are what `bucket()` projects into, so
 * naming them here is the single source, not a copy of one. `bucket()` returns
 * a member of its ladder by construction — it starts at `bounds[0]` and only
 * ever assigns from `bounds` — so exact membership is the true domain and a
 * range was always an approximation of it.
 */
const NUMERIC_DOMAINS: Readonly<Record<string, NumericDomain>> = {
  // `bucket()` output. Membership, because that is what the projection produces.
  'shape/depth': { set: DEPTH_BUCKETS },
  'shape/nodeCount': { set: NODE_BUCKETS },
  // `schema.ts`: Type.Integer({ minimum: 1, maximum: 4 }). COPIED through
  // `stripQuery` rather than bucketed — `depth: q.traverse.depth` — so this is
  // the only bound it has.
  'traverse/depth': { range: [1, 4] },
  maxDepth: { range: [1, 4] },
  // Type.Integer({ minimum: 0, maximum: 7 }) — an index into view state.
  valueFrom: { range: [0, 7] },
};

/** The most specific domain declared for a path: two segments, then one, then
 *  the floor under a key nothing declares. */
function domainFor(path: string): NumericDomain {
  const parts = path.split('/');
  const two = parts.length >= 2 ? `${parts[parts.length - 2]}/${parts[parts.length - 1]}` : '';
  return NUMERIC_DOMAINS[two]
    ?? NUMERIC_DOMAINS[parts[parts.length - 1] ?? '']
    ?? { range: [0, MAX_GENOME_NUMBER] };
}

/** For a number under a key nothing declares. Every legitimate one is a small
 *  structural count, and this is the floor under that claim. */
const MAX_GENOME_NUMBER = 4096;

/**
 * Every string in a genome is a member of the closed vocabulary.
 *
 * Task `0124` clause 1. Keys as well as values: a genome's object keys are
 * either fixed by `PoolGenome` or rebuilt from a capability descriptor's own
 * `configShapes`, so a key outside the vocabulary is a path by which content
 * reached the genome through a map rather than through a field.
 *
 * Numbers and booleans are unchecked on purpose. `bucket()` already coarsens
 * the only two numbers a genome carries — depth and node count — to a fixed
 * ladder, and a boolean cannot carry content.
 */
export function assertClosedVocabulary(genome: PoolGenome): readonly string[] {
  const vocabulary = closedVocabulary();
  const outside: string[] = [];
  /** This value's ancestors — membership means it contains itself. */
  const onPath = new Set<object>();
  /**
   * Walked to completion already, so its violations are recorded — **under
   * which parent key**.
   *
   * @Codex, P2 on `4053a0f`: *"Fresh evidence after narrowing the shared-value
   * cache for canonical IDs is that numeric validation is also path-dependent:
   * an externally supplied genome can reuse the same `{ depth: 5 }` object
   * first at `/shape`, where `5` is a valid `DEPTH_BUCKETS` value, and later at
   * `/structure/query/traverse`, where the permitted range is only 1–4. The
   * first walk adds the object to `done`, while this second path remains
   * `cacheable`, so the invalid traversal depth is skipped … Cache by object
   * plus numeric validation context, or disable memoization wherever
   * `domainFor(path)` can differ."*
   *
   * Right, and it is the previous round's narrowing stopping one dimension
   * short: it made the cache path-aware for STRINGS and left numbers reading
   * the same object as already-checked.
   *
   * The context is the parent key, and that is exact rather than conservative.
   * `domainFor` reads at most the last two segments of a number's own path, so
   * for a value at `…/k` the domains of its numeric children resolve from `k`
   * and the child's key — and recursively, the whole subtree's numeric
   * validation depends on nothing above `k`. Caching per `(value, parent key)`
   * is therefore as small as it can be without being wrong.
   */
  const done = new Map<object, Set<string>>();

  const walk = (value: unknown, path: string, depth: number): void => {
    /**
     * Over-depth is a REFUSAL, not a stopping point.
     *
     * @Codex, P2 on #184: *"An unvalidated collection filter can nest more than
     * 32 `not` nodes while remaining far below `MAX_VALUES`; `stripQuery()`
     * recursively preserves the leaf, but this cutoff prevents the leaf's
     * role/operator strings from being checked. A private or otherwise
     * non-vocabulary string at that depth therefore passes `assertNoLeak`."*
     *
     * Exactly right, and it is the worst shape a guard can have: the bound was
     * written to stop a hostile input from spinning the walk, and it turned the
     * hostile input into the way past the check. A depth bound on a PARSER may
     * stop; a depth bound on a GUARD must refuse, because what it has not
     * looked at is precisely what it was asked about.
     *
     * `outside.length` still stops, and that is a different thing: sixteen
     * failures already refuse the genome, so the seventeenth changes no
     * outcome — it only lengthens the message.
     */
    if (depth > MAX_GENOME_DEPTH) {
      outside.push(`${path}: nested deeper than ${MAX_GENOME_DEPTH}, so nothing `
        + 'below it could be checked');
      return;
    }
    if (outside.length > 16) return;
    if (typeof value === 'string') {
      if (!vocabulary.has(value) && !(CANONICAL_ID.test(value) && bearsNodeId(path))) {
        outside.push(`${path}: "${value}"`);
      }
      return;
    }
    /**
     * Numbers, checked rather than waved through. @Codex, P2 on the same
     * review: *"this primitive early return admits arbitrary numbers copied
     * into the genome, such as `node-slot.maxDepth: 123456789` … Those values
     * can encode private state despite the corresponding schema fields having
     * small fixed bounds."*
     *
     * A number is a narrower channel than a string and it is still a channel:
     * a genome carries a handful of them, every one a small structural count,
     * and 32 bits of `maxDepth` is 32 bits that came from somewhere.
     *
     * Bounded rather than enumerated, because the legitimate ones are counts
     * whose domains differ — bucket ladders, depths, indices — and a list of
     * exact permitted values would be a second copy of `vocabulary.ts` that
     * drifts. A non-negative integer under 4,096 cannot carry much and is what
     * every real field here holds; `0127` is where naming them individually
     * belongs, with the six copied scalars.
     */
    if (typeof value === 'number') {
      const domain = domainFor(path);
      if ('set' in domain) {
        if (!domain.set.includes(value)) {
          outside.push(`${path}: ${value} is not one of ${domain.set.join(', ')}`);
        }
        return;
      }
      const [low, high] = domain.range;
      if (!Number.isInteger(value) || value < low || value > high) {
        outside.push(`${path}: ${value} is outside ${low}..${high}`);
      }
      return;
    }
    if (!value || typeof value !== 'object') return;
    /**
     * A cycle is a REFUSAL, exactly like over-depth above it.
     *
     * @Codex, P2 on #184: *"When `assertClosedVocabulary()` receives an
     * externally supplied retrieved genome whose structure cycles through valid
     * structural keys — for example, a region included in its own `children`
     * array — this early return reports no violation because every encountered
     * key and scalar is otherwise vocabulary. `assertNoLeak()` consequently
     * accepts a value that `project()` could never produce, and recursive
     * consumers such as genome canonicalization can then terminate with a raw
     * stack overflow."*
     *
     * Right, and the argument is already written twenty lines above this, about
     * the depth bound: *"a depth bound on a PARSER may stop; a depth bound on a
     * GUARD must refuse, because what it has not looked at is precisely what it
     * was asked about."* One `Set` was doing two jobs — stopping a cycle and
     * skipping a repeat — and silence is the right answer to only one of them.
     *
     * So there are two sets now. `onPath` holds this value's own ancestors, so
     * finding a value in it means the value contains itself: a cycle, refused.
     * `done` holds values already walked to completion by another route, whose
     * violations are therefore already recorded: skipping those is safe, and it
     * is what stops a shared subtree from being walked exponentially.
     */
    if (onPath.has(value)) {
      outside.push(`${path}: the genome contains a cycle`);
      return;
    }
    // Consulted only where the answer cannot depend on the route — see
    // `pathDecidesValidity`. Under `/partition` the same subtree validates
    // differently by path, so a cache hit there would be the skip @Codex found.
    const cacheable = !pathDecidesValidity(path);
    // The parent key — see `done`. `''` for the root, which has no parent and
    // is walked once anyway.
    const under = path.slice(path.lastIndexOf('/') + 1);
    if (cacheable && done.get(value)?.has(under)) return;
    onPath.add(value);
    if (Array.isArray(value)) {
      value.forEach((item, i) => { walk(item, `${path}/${i}`, depth + 1); });
      onPath.delete(value);
      if (cacheable) remember(done, value, under);
      return;
    }
    for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
      /**
       * No canonical-id exemption for KEYS, because nothing mints one.
       *
       * @Codex, P2 on #184: *"keys matching `n<digits>` are still exempt at
       * every path. An externally supplied retrieved genome can therefore
       * contain a structure such as `{ n123: 'card' }`; the key is accepted
       * despite not being a partition reference, and `assertNoLeak()` returns
       * without noticing even when `n123` is a supplied private value."*
       *
       * The companion to narrowing the VALUE exemption, and I left it out of
       * that change on the grounds that the finding was about values. It was
       * about an exemption that suppresses the guard behind it, and a key
       * suppresses it exactly as well.
       *
       * Removed rather than narrowed, which is the tighter answer and the one
       * the producer supports: a genome's keys come from the fixed `PoolGenome`
       * shape and from a capability's `configShapes`, both closed vocabularies.
       * `renumber` and `canonicalBranch` mint canonical ids as VALUES and never
       * as keys. Checked against three projected genomes including a
       * partitioned one — the only kind that carries canonical ids at all — and
       * none has a single such key.
       */
      if (!vocabulary.has(key) && !STRUCTURAL_KEYS.has(key)) {
        outside.push(`${path}: key "${key}"`);
      }
      walk(item, `${path}/${key}`, depth + 1);
    }
    onPath.delete(value);
    if (cacheable) remember(done, value, under);
  };

  walk(genome, '', 0);
  return outside;
}

/**
 * The field names `PoolGenome` itself declares.
 *
 * Not vocabulary — they are the shape of the record rather than anything a
 * projection chose — but they are strings that appear as keys, so the check
 * above needs them enumerated. Any key not here and not a vocabulary term
 * arrived from a map whose keys came from somewhere, which is the question.
 */
const STRUCTURAL_KEYS = new Set<string>([
  'vocabularyVersion', 'target', 'cardinality', 'mediaWeight', 'context',
  'density', 'typography', 'palette', 'structure', 'partition', 'template', 'shape',
  'kind', 'children', 'items', 'slots', 'spec', 'id', 'via', 'maxDepth',
  'mutations', 'affordances', 'effects', 'source', 'role', 'mode', 'extent',
  'edge', 'rest', 'attachment', 'capability', 'config', 'branches', 'unset',
  'show', 'depth', 'nodeCount', 'capabilities', 'roleKinds', 'writable',
  'anchor', 'placement', 'query', 'region', 'body', 'aside', 'header',
  'footer', 'main',
  // The query and its clauses, and the media/binding fields beside them. Added
  // after the battery reported them, not before it — see `closedVocabulary`.
  'flow', 'fit', 'aspect', 'bindings', 'direction', 'filter', 'where', 'op',
  'valueFrom', 'sort', 'groupBy', 'aggregate', 'fn', 'derive', 'of', 'through',
  'labelRole', 'mutation', 'version',
  /**
   * The four a browser found that two fixtures did not.
   *
   * `e2e/pool-gesture.mjs` reported nine refusals against the demo's own
   * generated specs while `packages/spec`'s battery was green — which is the
   * measure of how narrow two fixtures are, and the reason that harness now
   * asserts membership over the demo's real genome rather than trusting this
   * list to have been written completely.
   */
  'collapsible', 'invocation', 'args', 'not',
  /**
   * The FIFTH instance, and the reason this list now has a test that walks the
   * projector's output rather than another paragraph promising to be careful.
   *
   * @Codex, P2 on `857dbfa`: *"When a kept accepted spec contains a compound
   * filter, subgroup, traversal, or optional media node, `stripQuery()` /
   * `strip()` emits keys such as `all`, `any`, `subGroupBy`, `traverse`, and
   * `optional`, but this allowlist omits them. `assertNoLeak()` therefore
   * rejects the projector's own valid output as outside the closed vocabulary,
   * preventing those accepted layouts from entering the new private pool."*
   *
   * Every one is written by this file, `all` and `any` twenty lines apart from
   * `not`, which is here. A compound filter is not exotic — it is what a person
   * gets from two conditions on one view.
   *
   * Not DERIVED from the projector, and that is deliberate against the review's
   * own alternative: this list is a gate, and a gate generated by the thing it
   * gates can widen itself. What replaces the promise is
   * `pool-structural-keys.test.ts`, which projects a spec exercising each of
   * these constructs and asserts the projection is admitted — a fixture, so
   * the sixth key still has to be noticed, but noticed by a red test rather
   * than by a person in a browser.
   */
  'all', 'any', 'subGroupBy', 'traverse', 'optional',
  /**
   * The polymorphic item GUARD, sixth of the same shape. @Codex, P2 on
   * `41e9da0`: the projection emitted each item's `spec` and dropped the
   * `when.has` that decides which template an object gets, so two collections
   * that draw different objects differently were one genome.
   *
   * `has` with it: the guard is `{ has: <role> }`, and the role inside goes
   * through `fromClosedSet` like every other. The KEY still has to be
   * enumerated — `assertClosedVocabulary` walks keys as well as values, which
   * is what makes an author-invented key a leak rather than a curiosity.
   */
  'when', 'has',
  /**
   * `channels`, which `strip` has emitted since capabilities had any.
   *
   * The FOURTH instance of task `0134`'s shape, and the sharpest: this is a key
   * the projection writes itself, two lines below where it writes `config` —
   * and `config` is in this list while `channels` was not. So no genome
   * carrying a capability channel could ever enter the pool, whatever its
   * module declared.
   *
   * It survived because no fixture had one. `packages/spec`'s battery projects
   * the reference sheet, whose capability nodes carry no channels, and
   * `module-registry.test.ts` composes a channelled module and asserts
   * `validateSpec` and `project` without ever calling `assertNoLeak`. Each was
   * green about the half it looked at.
   *
   * Found by writing `0134`'s clause with a channel in it and reading which
   * name the refusal actually gave — the leak was `channels`, not the module's
   * own `ledger-head`.
   */
  'channels',
  /**
   * `purpose`, and the value with it — a functional break, not a hardening gap.
   *
   * @Codex, P2 on #184: *"When a generated lens surfaces relationships,
   * `baseline.ts` emits a region with `purpose: 'relations'`, and `project()`
   * preserves both strings. Neither `purpose` nor `relations` is admitted by
   * this vocabulary, so `assertClosedVocabulary()` reports them as outside and
   * `assertNoLeak()` throws on Keep; the lens remains accepted locally, but
   * `contributeAcceptedLens()` drops every such genome from the new pool
   * producer."*
   *
   * Verified rather than assumed: `baseline.ts` emits it and `pool.ts` projects
   * it, and neither the battery's two fixtures nor the demo's own seeded lens
   * carries a relations region — so three clauses written to catch exactly this
   * omission all missed it, and the harness clause added for it is now a fourth
   * that would.
   *
   * `relations` goes in the values because a genome carries it as one; the
   * schema declares exactly this literal.
   */
  'purpose',
]);

/**
 * Prove no private identifier survived.
 *
 * **Membership decides; the needles only name.** That inversion is task `0124`,
 * and the first attempt at it kept the two as independent checks — which broke
 * within one browser run, in exactly the way that shows why the old length
 * filter existed.
 *
 * The substring search cannot survive short needles at any threshold, and
 * membership is not what saves it: a needle that is not a vocabulary term can
 * still sit INSIDE one. Removing the filter admitted the needle `a`, which is
 * not vocabulary and appears in `card` — so `e2e/pool-gesture.mjs` reported
 * `0 genomes before, 0 after` and every contribution was refused.
 *
 * Once membership holds there is nothing left for a substring search to find.
 * Every string in the genome is a vocabulary term or a canonical node id, so a
 * private value can only be present as a whole string, and a whole string that
 * is a vocabulary term is the coincidence this task exists to stop treating as
 * evidence. So membership returning clean IS the proof, and the needles are
 * spent on the message instead: when membership fails, the offending value is
 * matched against the host's private strings so the refusal can say *"the title
 * of the note you were looking at reached the genome"* rather than *"a string
 * outside the vocabulary"*.
 */
export function assertNoLeak(genome: PoolGenome, needles: string[]): void {
  const outside = assertClosedVocabulary(genome);
  if (!outside.length) return;
  throw new AbstractionError(outside.map((o) => {
    const named = needles.find((n) => n.length > 0 && o.includes(n));
    return named
      ? `private value "${named}" found in genome at ${o}`
      : `${o} is outside the closed vocabulary decision 0039 requires`;
  }));
}
